import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} productLink - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.trigger.addEventListener('mousedown', (e) => e.preventDefault());

    // Set up initial event listeners based on current breakpoint
    this.#handleBreakpointChange();

    // Listen for breakpoint changes
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up listeners
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Open the quick-add modal
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  /**
   * Set up desktop event listeners (click/focus)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;
    trigger.addEventListener('focus', this.#handleTriggerFocus);
    trigger.addEventListener('blur', this.#handleTriggerBlur);
    dialog.addEventListener('focusout', this.#handleDialogFocusOut);
  }

  /**
   * Remove desktop event listeners
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger, dialog } = this.refs;
    trigger.removeEventListener('focus', this.#handleTriggerFocus);
    trigger.removeEventListener('blur', this.#handleTriggerBlur);
    dialog.removeEventListener('focusout', this.#handleDialogFocusOut);
  }

  #handleTriggerFocus = () => {
    if (!this.refs.dialog.open) this.showDialog();
  };

  #handleTriggerBlur = (e) => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    if (e.relatedTarget && dialog.contains(e.relatedTarget)) return;
    this.closeDialog();
  };

  #handleDialogFocusOut = (e) => {
    const { trigger, dialog } = this.refs;
    if (!e.relatedTarget) return;
    if (e.relatedTarget === trigger) return;
    if (dialog.contains(e.relatedTarget)) return;
    this.closeDialog();
  };

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    // Remove existing listeners
    this.#removeDesktopListeners();

    // Set up desktop focus listeners only (mobile uses on:click in template)
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    // Spacing constants
    const BUTTON_GAP = 10; // Gap between button and dialog
    const CONTAINER_GAP = 10; // Gap from container edges
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    // Get container bounds
    const containerRect = hotspotsContainer?.getBoundingClientRect();

    // Get button dimensions
    const triggerRect = trigger.getBoundingClientRect();

    // To get dialog dimensions, we need to temporarily show it invisibly
    // Show dialog invisibly to measure it
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    // Reset dialog state
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
    // Calculate button position relative to container
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    if (isMobileBreakpoint()) {
      const buttonWidth = triggerRect.width;
      const idealLeft = (buttonWidth - dialogWidth) / 2;
      const minLeft = -buttonLeft + CONTAINER_GAP;
      const maxLeft = containerRect.width - buttonLeft - dialogWidth - CONTAINER_GAP;
      const clampedLeft = Math.max(minLeft, Math.min(maxLeft, idealLeft));

      dialog.style.setProperty('left', `${clampedLeft}px`);
      dialog.style.setProperty('top', '100%');
      dialog.style.setProperty('translate', 'none');
      dialog.style.removeProperty('right');
      dialog.style.removeProperty('bottom');
      dialog.style.removeProperty('--dialog-vertical-offset');
      dialog.dataset.placement = 'center,bottom';
      return new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Calculate available space
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    // Determine horizontal placement
    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    // Determine vertical placement
    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop; // Default to top-aligned
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        // If top-aligned overflows bottom
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          // If bottom-aligned overflows top
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          // If bottom-aligned overflows bottom
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          // If top-aligned overflows top
          if (dialogStartY < CONTAINER_GAP) {
            verticalOffset = CONTAINER_GAP - dialogStartY;
          }
          y = 'bottom';
        }
      }
    } else {
      // For center horizontal: position below or above button
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        // If neither fits well, choose based on button position
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    // Set placement data attribute
    dialog.dataset.placement = `${x},${y}`;

    // Apply vertical offset if needed to keep dialog in bounds
    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    // Return a promise that resolves after a few ticks to ensure styles are applied
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click - on mobile/touch devices opens quick-add, on desktop opens dialog
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    const { dialog } = this.refs;

    if (dialog?.open) {
      this.closeDialog();
      return;
    }

    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      const quickAddComponent = this.querySelector('quick-add-component');
      if (quickAddComponent) {
        this.#openQuickAddModal();
      } else {
        this.showDialog();
      }
    } else {
      this.showDialog();
    }
  };

  showDialog = async () => {
    document.querySelectorAll('product-hotspot-component').forEach((hotspot) => {
      if (hotspot !== this && hotspot.refs?.dialog?.open) {
        hotspot.closeDialog();
      }
    });

    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();

    dialog.dataset.showing = 'true';
    dialog.show();
    if (dialog.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.style.removeProperty('left');
    dialog.style.removeProperty('top');
    dialog.style.removeProperty('translate');
    dialog.close();
    setTimeout(() => trigger.blur(), 0);
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    // we need to use a data-attribute to keep transition-behavior working only when open
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

// Mobile accordions — must run before customElements.define
// to avoid MissingRefError (thrown during upgrade) stopping execution
function initHotspotAccordions() {
  document.querySelectorAll('.section-product-hotspots').forEach((section) => {
    const mq = window.matchMedia('(max-width: 749px)');

    function buildAccordions() {
      section.querySelector('.hotspots-accordion-list')?.remove();
      if (!mq.matches) return;

      const hotspots = section.querySelectorAll('product-hotspot-component');
      if (!hotspots.length) return;

      const list = document.createElement('div');
      list.className = 'accordion accordion--plus border-style spacing-style accordion--dividers hotspots-accordion-list';

      hotspots.forEach((hotspot) => {
        const dialogContent = hotspot.querySelector('.hotspot-dialog .hotspot-dialog__content');
        if (!dialogContent) return;

        const titleEl = dialogContent.querySelector('.hotspot-dialog__content-title');
        const titleHTML = titleEl ? titleEl.innerHTML : '';

        const accordionCustom = document.createElement('accordion-custom');

        const details = document.createElement('details');
        details.className = 'details';

        const summary = document.createElement('summary');
        summary.className = 'details__header';
        summary.innerHTML = `<span class="hotspot-accordion__title h5">${titleHTML}</span><span class="svg-wrapper icon-plus" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path><path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path></svg></span>`;

        const panel = document.createElement('div');
        panel.className = 'details-content';

        const inner = document.createElement('div');
        inner.className = 'hotspot-accordion__inner';

        const cloned = dialogContent.cloneNode(true);
        cloned.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
        cloned.querySelector('.hotspot-dialog__content-title')?.remove();
        cloned.querySelector('.hotspot-dialog__img-wrapper')?.remove();
        inner.appendChild(cloned);

        panel.appendChild(inner);
        details.appendChild(summary);
        details.appendChild(panel);
        accordionCustom.appendChild(details);
        list.appendChild(accordionCustom);
      });

      if (!list.children.length) return;

      section.querySelector('.section-product-hotspots__content').insertAdjacentElement('afterend', list);
    }

    buildAccordions();
    mq.addEventListener('change', buildAccordions);
  });
}

initHotspotAccordions();

// Register custom element
customElements.define('product-hotspot-component', ProductHotspotComponent);
